/**
 * The console rendering of `/a7y-addis-status`, as THREE TABLES GROUPED BY THE
 * JOB TO BE DONE (A7Y-3812) — Platform Health, Release Pipeline, Dev Pipeline —
 * with one row per workflow file.
 *
 * The board has been cut three ways in as many revisions, so the reason for
 * this one is worth stating. Four system cards showed everything and cost
 * ~1400px. One row per system fixed the size but could only ever show the
 * workflows those four systems own — `canonical-verify.yml`,
 * `state-invariants.yml`, `bq-schema-sync.yml` and the APHID observers belong
 * to no ADDIS system and were therefore invisible on a page titled "Dev
 * Automation Overview". Grouping by job covers every workflow that matters and
 * still fits, because a row is one line.
 *
 * The per-system cut has NOT been deleted: `systems` still drives the four
 * detail pages and their lights. A system is a piece of automation with a page
 * and a queue; a group is a job. Both are real, and the server derives their
 * lights with the same `runHealth` rule so they cannot disagree.
 *
 * The production chain, the two `ADDIS status` straps and the footnote about how
 * lights are computed are all GONE (TPF, 2026-09-09): none was worth scanning
 * on every visit. The chain's six figures are still computed — APD's light is
 * derived from them server-side, and they still ride in the payload that mirrors
 * `/a7y-addis-status` — they are simply not something to read past three times
 * a day. What remains on this page is the tables and nothing else.
 *
 * Every light and reason is still decided server-side; this file renders what
 * it is given and infers nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { GroupTable } from './addis-status-parts';

/** How many grey placeholder rows to show while the status query is in flight.
 *  A LAYOUT number, not a group or workflow count. */
const SKELETON_PLACEHOLDER_ROWS = 3;

export function AddisStatusBoard() {
  const status = useQuery({
    queryKey: ['addis-status'],
    queryFn: () => api.getAddisStatus(),
    refetchInterval: 60_000,
  });

  if (status.isError) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        ADDIS status failed to load. Check the Worker logs for /admin/autobuild/addis-status.
      </div>
    );
  }

  if (!status.data) {
    return (
      <div className="space-y-4">
        {Array.from({ length: SKELETON_PLACEHOLDER_ROWS }).map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-900" />
        ))}
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Reading ADDIS status…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Platform health has its own page under the Platform Health nav
          section (TPF, 2026-09-09) — a table of APHID observers was never
          "dev automation", and this page is the two pipelines. Filtered by key
          rather than by index so a fourth group lands here by default. */}
      {status.data.groups
        .filter((group) => group.key !== 'platform-health')
        .map((group) => (
          <GroupTable key={group.key} group={group} />
        ))}
    </div>
  );
}
